import { TFunction } from 'i18next';

// Mapping of exercise names to translation keys
const exerciseKeyMap: Record<string, string> = {
  "Les putts à plat": "puttingTraining.exercises.putts-plat",
  "Les putts en montée": "puttingTraining.exercises.putts-montee",
  "Les Putts en descente": "puttingTraining.exercises.putts-descente",
  "Les Putts en Pente gauche droite": "puttingTraining.exercises.putts-pente",
  "Les Putts en Pente droite gauche": "puttingTraining.exercises.putts-pente-droite",
  "Chipping balle levée sur terrain plat": "chippingTraining.exercises.chipping-plat",
  "Chipping balle levée Pente droite gauche": "chippingTraining.exercises.chipping-pente-droit",
  "Chipping balle levée Pente gauche droite": "chippingTraining.exercises.chipping-pente-gauche",
  "Chipping balle roulée terrain plat": "chippingTraining.exercises.chipping-roulee-plat",
  "Chipping balle roulée pente droite gauche": "chippingTraining.exercises.chipping-roulee-pente",
  "Chipping balle roulée pente gauche droite": "chippingTraining.exercises.chipping-roulee-pente-gauche"
};

// French fallback names for when translations are not available
const frenchFallbacks: Record<string, string> = {
  "putts-plat": "Les putts à plat",
  "putts-montee": "Les putts en montée",
  "putts-descente": "Les putts en descente", 
  "putts-pente": "Les putts en pente gauche droite",
  "putts-pente-droite": "Les putts en pente droite gauche",
  "chipping-plat": "Chipping balle levée sur terrain plat",
  "chipping-pente-droit": "Chipping balle levée Pente droite gauche",
  "chipping-pente-gauche": "Chipping balle levée Pente gauche droite",
  "chipping-roulee-plat": "Chipping balle roulée terrain plat",
  "chipping-roulee-pente": "Chipping balle roulée pente droite gauche",
  "chipping-roulee-pente-gauche": "Chipping balle roulée pente gauche droite",
  "wedging-cercles": "Wedging court",
  "wedging-pente-gauche": "Wedging court en pente gauche droite",
  "wedging-mid": "Wedging mi-distance",
  "wedging-pente-droite": "Wedging court en pente droite gauche",
  "wedging-mid-pente-gauche": "Wedging mi-distance en pente gauche droite",
  "wedging-mid-pente-droite": "Wedging mi-distance en pente droite gauche",
  "bunker-court": "Bunkers courts",
  "bunker-long": "Bunkers longs",
  "iron-green-attack": "Attaque de green 120 m",
  "iron-green-attack-100": "Attaque de green 100 m"
};

// Function to translate exercise names
export const translateExerciseName = (exerciseName: string, t: TFunction): string => {
  console.log('translateExerciseName called with:', exerciseName);
  
  // First check if it's already a translation key
  if (exerciseName.startsWith('puttingTraining.exercises.') || exerciseName.startsWith('chippingTraining.exercises.')) {
    const translated = t(exerciseName, { ns: 'common' });
    console.log('Translating key:', exerciseName, 'to:', translated);
    
    // If translation returns the same key, it means translation doesn't exist
    if (translated === exerciseName) {
      // Extract the test ID and use French fallback
      const testId = exerciseName.replace('puttingTraining.exercises.', '').replace('chippingTraining.exercises.', '');
      const fallback = frenchFallbacks[testId] || exerciseName;
      console.log('Translation not found, using fallback:', fallback);
      return fallback;
    }
    
    return translated;
  }
  
  // Check if it's a test ID and try to translate it first
  if (frenchFallbacks[exerciseName]) {
    // Try to get the translation for this ID
    let key: string;
    if (exerciseName.startsWith('putts-')) {
      key = `puttingTraining.exercises.${exerciseName}`;
    } else if (exerciseName.startsWith('chipping-')) {
      key = `chippingTraining.exercises.${exerciseName}`;
    } else if (exerciseName.startsWith('wedging-')) {
      key = `wedgingTraining.exercises.${exerciseName}`;
    } else if (exerciseName.startsWith('bunker-')) {
      key = `bunkerTraining.exercises.${exerciseName}`;
    } else if (exerciseName.startsWith('iron-')) {
      key = `swingTraining.exercises.${exerciseName}`;
    } else if (exerciseName.startsWith('fairwayWood-')) {
      key = `swingTraining.exercises.${exerciseName}`;
    } else if (exerciseName.startsWith('driver-')) {
      key = `swingTraining.exercises.${exerciseName}`;
    } else {
      key = `chippingTraining.exercises.${exerciseName}`;
    }
    
    const translated = t(key, { ns: 'common' });
    if (translated !== key) {
      console.log('Translating ID:', exerciseName, 'via key:', key, 'to:', translated);
      return translated;
    }
    
    console.log('Using French fallback for ID:', exerciseName, '->', frenchFallbacks[exerciseName]);
    return frenchFallbacks[exerciseName];
  }
  
  // Otherwise, look up the translation key
  const key = exerciseKeyMap[exerciseName];
  if (key) {
    const translated = t(key, { ns: 'common' });
    console.log('Translating name:', exerciseName, 'via key:', key, 'to:', translated);
    
    // If translation returns the same key, use the original French name
    if (translated === key) {
      console.log('Translation not found, keeping original French name:', exerciseName);
      return exerciseName;
    }
    
    return translated;
  }
  
  console.log('No translation found for:', exerciseName);
  return exerciseName;
};

// Function to get translated name by ID
export const getExerciseNameById = (id: string, t: TFunction): string => {
  // Try to get the translation key for this ID
  const key = `chippingTraining.exercises.${id}`;
  const translated = t(key, { ns: 'common' });
  
  // If translation exists, return it
  if (translated !== key) {
    return translated;
  }
  
  // Otherwise, use the fallback system
  return frenchFallbacks[id] || id;
};
